<script setup lang="ts">
import { computed } from 'vue'

import type { CohortCell, CohortGrid } from '../../models/cohort.types'

import { useI18n } from '@/shared/i18n'
import { dt, useFormat } from '@/shared/lib/format'
import { Button, Skeleton, type UiAsyncState } from '@/shared/ui'

// v3 `AppAcquisitionCohort.vue` layout: an installs pill, then one pill per cohort period. Monthly
// shows period 0 as the "Month 1" 100% anchor; weekly starts at "Week 1" — period 0 only feeds the
// installs column (v3 `:4-20`). Columns are positional by `cohort_period`.
// A heatmap, not a data table: no grid lines, no zebra rows — every cell is a pill that fills its
// slot and the pills are separated by the table's border-spacing.
const TONES = [
  'bg-[color:var(--ui-chart-cohort-1)]',
  'bg-[color:var(--ui-chart-cohort-2)]',
  'bg-[color:var(--ui-chart-cohort-3)]',
  'bg-[color:var(--ui-chart-cohort-4)]',
  'bg-[color:var(--ui-chart-cohort-5)]',
  'bg-[color:var(--ui-chart-cohort-6)]',
  'bg-[color:var(--ui-chart-cohort-7)]',
] as const
const PILL = 'flex h-8 w-full items-center justify-center whitespace-nowrap rounded-md px-2 text-sm'
const NEUTRAL_PILL = `${PILL} bg-border text-[color:var(--ui-table-cell-fg)]`
const SKELETON_ROWS = 6

const props = defineProps<{ grid: CohortGrid; frequency: 'week' | 'month'; state: UiAsyncState }>()
const emit = defineEmits<{ retry: [] }>()
const { t } = useI18n()
const format = useFormat()

const periods = computed(() => {
  const first = props.frequency === 'month' ? 0 : 1
  return Array.from({ length: Math.max(0, props.grid.width - first) }, (_, index) => {
    const period = index + first
    return {
      period,
      header: t(
        props.frequency === 'month'
          ? 'commerceAnalytics.appAcquisition.cohort.monthPeriod'
          : 'commerceAnalytics.appAcquisition.cohort.weekPeriod',
        { n: props.frequency === 'month' ? period + 1 : period },
      ),
    }
  })
})
const rows = computed(() =>
  props.grid.rows.map((row) => ({
    key: row.start_date,
    start:
      props.frequency === 'month'
        ? dt.formatBucketLabel(row.start_date, 'month_of_year', format.locale)
        : format.formatDate(row.start_date, { dateStyle: 'long' }),
    installs: row.cells.find((cell) => cell.cohort_period === 0)?.mmp_app_installs ?? 0,
    cells: new Map(row.cells.map((cell) => [cell.cohort_period, cell])),
  })),
)
const skeletonColumns = computed(() => Math.max(periods.value.length, 6))

function toneClass(cell: CohortCell): string {
  if (cell.cohort_period === 0) return 'bg-info'
  const index = Math.floor((cell.intensity ?? 0) * (TONES.length - 1))
  return TONES[Math.min(TONES.length - 1, Math.max(0, index))] ?? TONES[0]
}
function percent(cell: CohortCell): string {
  const digits = cell.cohort_period === 0 ? 0 : 2
  return format.formatPercent(cell.percent / 100, {
    scale: 'ratio',
    minFrac: digits,
    maxFrac: digits,
  })
}
</script>

<template>
  <div class="cohort-grid w-full overflow-x-auto">
    <div
      v-if="state === 'error'"
      class="cohort-grid__state grid place-items-center gap-sm p-lg text-muted"
      role="alert"
    >
      <span>{{ t('commerceAnalytics.appAcquisition.cohort.error') }}</span>
      <Button variant="outline" size="sm" @click="emit('retry')">
        {{ t('commerceAnalytics.appAcquisition.cohort.retry') }}
      </Button>
    </div>
    <div
      v-else-if="state !== 'loading' && rows.length === 0"
      class="cohort-grid__state p-lg text-center text-muted"
    >
      {{ t('commerceAnalytics.appAcquisition.cohort.empty') }}
    </div>
    <table
      v-else
      class="w-full border-separate border-spacing-1 text-[color:var(--ui-table-cell-fg)]"
      :aria-label="t('commerceAnalytics.appAcquisition.cohort.title')"
      :aria-busy="state === 'loading'"
    >
      <thead>
        <tr>
          <td class="sticky left-0 z-raised min-w-36 bg-surface" />
          <th scope="col" class="min-w-20">
            <span class="sr-only">{{ t('commerceAnalytics.appAcquisition.cohort.installs') }}</span>
          </th>
          <template v-if="state === 'loading'">
            <th v-for="column in skeletonColumns" :key="column" scope="col" class="min-w-20">
              <Skeleton height="2rem" radius="0.375rem" />
            </th>
          </template>
          <th
            v-for="column in periods"
            v-else
            :key="column.period"
            scope="col"
            class="min-w-20 font-normal"
          >
            <span :class="NEUTRAL_PILL">{{ column.header }}</span>
          </th>
        </tr>
      </thead>
      <tbody v-if="state === 'loading'">
        <tr v-for="row in SKELETON_ROWS" :key="row">
          <td class="sticky left-0 z-raised bg-surface pe-4">
            <Skeleton height="1rem" width="6rem" />
          </td>
          <td v-for="column in skeletonColumns + 1" :key="column">
            <Skeleton height="2rem" radius="0.375rem" />
          </td>
        </tr>
      </tbody>
      <tbody v-else>
        <tr v-for="row in rows" :key="row.key">
          <th
            scope="row"
            class="sticky left-0 z-raised whitespace-nowrap bg-surface ps-2 pe-4 text-start text-sm font-normal"
          >
            {{ row.start }}
          </th>
          <td>
            <span :class="NEUTRAL_PILL">{{ format.formatInteger(row.installs) }}</span>
          </td>
          <td v-for="column in periods" :key="column.period">
            <span
              v-if="row.cells.get(column.period)"
              class="cohort-grid__cell text-[color:var(--ui-chart-cohort-label)]"
              :class="[PILL, toneClass(row.cells.get(column.period)!)]"
            >
              {{ percent(row.cells.get(column.period)!) }}
            </span>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
